import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { userApi } from "@/api"

interface UserState {
  userId: string | null;
  fullName: string | null;
  email: string | null;
  timezone: string | null;
  avatarUrl: string | null;
  isSuperuser: boolean;
  permissions: string[]; // 用户权限码列表（从 role 的 permissions 关系获取）
  roleCodes: string[]; // 用户系统角色 code 列表（从 role 的 code 字段获取，供 Help Center 等按角色过滤）
  settings: Record<string, any> | null;
  /**
   * 当前用户所属租户（current/info 的 tenantId，字符串 Snowflake）。
   * `null` = 明确没有租户（平台用户）；`undefined` = 还不知道（未加载）——判断「无租户」只认 `null`。
   */
  tenantId?: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  userId: null,
  avatarUrl: null,
  fullName: null,
  email: null,
  timezone: null,
  isSuperuser: false,
  permissions: [],
  roleCodes: [],
  settings: null,
  tenantId: undefined,
  loading: false,
  error: null,
}

/** 从 current-user 响应的 roles 关系里提取角色 code（健壮：容忍 undefined / 字符串形态 / 缺失 code）。 */
export function extractRoleCodes(rawRoles: unknown): string[] {
  if (!Array.isArray(rawRoles)) return []
  return rawRoles
    .map((r: any) => (typeof r === 'string' ? r : r?.code))
    .filter((c: unknown): c is string => typeof c === 'string' && c.length > 0)
}

export const fetchCurrentUser = createAsyncThunk(
  "user/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    const response = await userApi.getCurrentUser();
    const userData = response.data || response;
    const { id: userId, fullName, email, timezone, avatarUrl, isSuperuser, permissions: rawPermissions, roles: rawRoles, settings, tenantId: rawTenantId } = userData;
    if (!userId) {
      return rejectWithValue("User data is missing the id field");
    }
    // 从 role 关联的 permissions 中提取权限码
    // API 返回格式: permissions: Array<{ code: string, ... }> 或 string[]
    const permissions: string[] = Array.isArray(rawPermissions)
      ? rawPermissions.map((p: any) => typeof p === 'string' ? p : p.code).filter(Boolean)
      : [];
    const roleCodes = extractRoleCodes(rawRoles);
    // 字段缺失 = 不知道（不当成平台用户，免得把所有人的入口都藏掉）；显式 null 才是无租户
    const tenantId: string | null | undefined = !('tenantId' in userData) ? undefined : rawTenantId == null ? null : String(rawTenantId)
    return { userId, fullName, email, avatarUrl, timezone: timezone || 'America/New_York', isSuperuser: isSuperuser || false, permissions, roleCodes, settings: settings ?? null, tenantId };
  }
)

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<{ userId: string; fullName: string; email: string; timezone?: string; isSuperuser?: boolean }>) => {
      state.userId = action.payload.userId;
      state.fullName = action.payload.fullName;
      state.email = action.payload.email;
      state.timezone = action.payload.timezone || 'America/New_York';
      state.isSuperuser = action.payload.isSuperuser || false;
    },
    updateCurrentUserInfo: (state, action: PayloadAction<Partial<{ fullName: string; email: string; timezone: string; avatarUrl: string; isSuperuser: boolean }>>) => {
      // Update only the provided fields
      if (action.payload.fullName !== undefined) state.fullName = action.payload.fullName;
      if (action.payload.email !== undefined) state.email = action.payload.email;
      if (action.payload.timezone !== undefined) state.timezone = action.payload.timezone;
      if (action.payload.avatarUrl !== undefined) state.avatarUrl = action.payload.avatarUrl;
      if (action.payload.isSuperuser !== undefined) state.isSuperuser = action.payload.isSuperuser;
    },
    clearUserId: (state) => {
      state.userId = null;
      state.fullName = null;
      state.email = null;
      state.timezone = null;
      state.isSuperuser = false;
      state.permissions = [];
      state.roleCodes = [];
      state.settings = null;
      state.tenantId = undefined;
    },
    mergeCurrentUserSettings: (state, action: PayloadAction<Record<string, any>>) => {
      state.settings = { ...(state.settings ?? {}), ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.userId;
        state.fullName = action.payload.fullName;
        state.email = action.payload.email;
        state.avatarUrl = action.payload.avatarUrl;
        state.timezone = action.payload.timezone;
        state.isSuperuser = action.payload.isSuperuser;
        state.permissions = action.payload.permissions;
        state.roleCodes = action.payload.roleCodes;
        state.settings = action.payload.settings;
        state.tenantId = action.payload.tenantId;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load user info";
      });
  },
})

export const { setUserId, setUserInfo, updateCurrentUserInfo, clearUserId, mergeCurrentUserSettings } = userSlice.actions
/** 当前用户明确没有租户（平台用户）。未加载时返回 false，不误判。 */
export const selectIsTenantless = (state: { user: UserState }): boolean => state.user.tenantId === null

export default userSlice.reducer